import { useCallback, useState } from 'react';
import { Button } from '@cbhq/cds-web/buttons';
import { TextInput } from '@cbhq/cds-web/controls';
import { VStack } from '@cbhq/cds-web/layout';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@cbhq/cds-web/overlays';
import { TextBody } from '@cbhq/cds-web/typography';

import { useProject } from ':cloud/contexts/ProjectContext';
import { useUpdateBaseL3 } from ':cloud/queries/Base/useUpdateBaseL3';
import { useGetUser } from ':cloud/queries/UserQueries/useGetUser';
import { BaseL3Chain } from ':cloud/types/ts_types';
import { isValidContractAddress } from ':cloud/utils/paymaster';

type WhitelistModalProps = {
  onClose: () => void;
  isVisible: boolean;
  rollupName?: string;
  chain?: BaseL3Chain;
};

export function WhitelistModal({ rollupName, chain, onClose, isVisible }: WhitelistModalProps) {
  const { selectedProject } = useProject();
  const { activeOrg } = useGetUser();
  const orgId = activeOrg?.organizationId;
  const updateBaseL3Mutation = useUpdateBaseL3({
    orgId,
    projectId: selectedProject?.id,
  });
  const [address, setAddress] = useState('');
  const [error, setError] = useState('');

  const handleChange = useCallback((e) => {
    setError('');
    setAddress(e.target.value);
  }, []);

  const handleAddressBlur = useCallback(() => {
    if (!isValidContractAddress(address)) {
      setError('Invalid address');
    }
    const addresses = chain?.customizations?.contractAllowlist?.map((item) => item?.address);
    if (addresses?.includes(address)) {
      setError('Contract already allowlisted');
    }
  }, [address, chain?.customizations?.contractAllowlist]);

  const handleSubmit = useCallback(() => {
    const addresses = chain?.customizations?.contractAllowlist?.map((item) => item?.address);
    const updatedAllowlist = chain?.customizations?.contractAllowlist?.concat({ address });
    if (isValidContractAddress(address) && !addresses?.includes(address)) {
      updateBaseL3Mutation.mutate({
        uuid: chain?.uuid,
        chainId: chain?.id,
        contractAllowlist: updatedAllowlist,
      });
      onClose();
      setAddress('');
    }
  }, [
    address,
    chain?.customizations?.contractAllowlist,
    chain?.id,
    chain?.uuid,
    onClose,
    updateBaseL3Mutation,
  ]);

  const handleClose = useCallback(() => {
    setError('');
    setAddress('');
    onClose();
  }, [onClose]);

  const isDisabled = !address || !!error;
  return (
    <Modal visible={isVisible} onRequestClose={handleClose} width={450}>
      <ModalHeader title="Add whitelist address" />
      <ModalBody>
        <VStack spacingVertical={3} gap={3}>
          <TextBody as="p" color="foregroundMuted">
            You can allowlist which addresses are able to be called on{' '}
            <TextBody as="span">{rollupName}.</TextBody>
          </TextBody>
          <TextInput
            label="Address"
            helperText={
              error ||
              'Adding an address to the allowlist will prevent contracts not on the allowlist from being called on your network.'
            }
            onChange={handleChange}
            value={address}
            onBlur={handleAddressBlur}
            variant={error ? 'negative' : 'foregroundMuted'}
          />
        </VStack>
      </ModalBody>
      <ModalFooter
        primaryAction={
          <Button disabled={isDisabled} variant="primary" onPress={handleSubmit}>
            Confirm address
          </Button>
        }
      />
    </Modal>
  );
}
